import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { UseFormGetValues } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { getAllConstructionList } from '../../apicalls/construction';
import {
  TAttachableObject,
  TConstructionMinimal,
  TSelectItem
} from '../../types/common';
import { TSiteProcessPageLocationState } from '../../types/attendancManage';
import { checkResData, formatDatetime } from '../../utils/common';
import { parseNumber } from '../../utils/processAndTaskFormUtils';
import { getConstructionsSelectBoxData } from '../../utils/common';

export type Props = {
  getValues: UseFormGetValues<any>;
  setConstructListVal: any;
  constructListVal: TSelectItem;
  selectTab: number;
};

export const useHooks = (props: Props) => {
  const { getValues, setConstructListVal, constructListVal, selectTab } = props;

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // location.stateから工事IDを取得
  const location = useLocation();
  const state = location.state as TSiteProcessPageLocationState;
  const query = new URLSearchParams(useLocation().search);
  const constructIdQueryDef = parseNumber(query.get('constructId')) ?? null;
  const constructIdStateDef = constructListVal ? constructListVal.value : 0;
  const constructNameStateDef = constructListVal ? constructListVal.label : '';

  const constructListDefaultVal = {
    value: 0,
    label: '--'
  };

  // 工事選択リスト
  const [constructList, setConstructList] = useState<TSelectItem[]>([
    constructListDefaultVal
  ]);

  // {工事ID:顧客名}のオブジェクト
  // 工事選択の度に顧客名取得のためにAPIを実行しなくてよいように
  // 初回レンダリングの工事リスト取得に各工事の顧客名を保持しておく
  const [customerNameList, setCustomerNameList] = useState<TAttachableObject>(
    {}
  );

  // 選択した工事の顧客名
  const [customerName, setCustomerName] = useState('');

  // 選択した工事ID
  const [constructId, setConstructId] = useState(0);

  // 工程追加モーダルの表示制御
  const [isOpenAdd, setIsOpenAdd] = useState(false);

  // ガントチャート用工程追加モーダルの表示制御
  const [isOpenAddOfGantt, setIsOpenAddOfGantt] = useState(false);

  //工程検索のフォーム入力値
  const [userInput, setUserInput] = useState('');

  // ガントチャートのローディング中判定
  // 「工程を追加」ボタンの制御がいるため親から渡す
  const [isLoadingGantt, setIsLoadingGantt] = useState(false);

  const [isDownloading, setIsDownloading] = useState(false);

  // 工事選択時のハンドラ
  const renderCurrentConstructView = () => {
    let currentConstructId = 0;

    // 選択欄から工事を指定した場合
    if (getValues('construct_id')) {
      currentConstructId = getValues('construct_id').value as number;
    } else {
      // 他ページから遷移する際に工事IDを指定した場合
      currentConstructId = constructIdStateDef;
    }

    // 未選択が設定されている場合はデフォルト値を設定
    if (currentConstructId === 0) {
      setConstructListVal(
        userInput ? { value: 0, label: userInput } : constructListVal
      );
      return;
    }

    if (currentConstructId) {
      setConstructId(currentConstructId);
      setCustomerName(customerNameList[String(currentConstructId)]);

      // 工事IDが該当する工事を工事選択プルダウンにセット
      constructList.forEach((construct: { value: number; label: string }) => {
        if (construct.value === currentConstructId) {
          setConstructListVal(construct);
        }
      });
    } else {
      setConstructId(0);
      setCustomerName('');
    }
  };

  // ガントチャート形式時の「工程の追加」クリック時のハンドラ
  const handleOnClickAddOfGantt = () => {
    setIsOpenAdd(true);
  };

  // 工事選択リスト設定（初回のみ）
  useEffect(() => {
    // 工事セレクトボックスを取得
    getConstructionsSelectBoxData(setConstructList, enqueueSnackbar, {
      value: 0,
      label: '--'
    });

    getAllConstructionList().then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const resConstructList: TConstructionMinimal[] = data.data;
          if (resConstructList && resConstructList.length > 0) {
            const objCustomerName: TAttachableObject = {};
            resConstructList.forEach((construct: TConstructionMinimal) => {
              objCustomerName[String(construct.id)] = construct.customer_name;
            });
            setConstructId(constructIdQueryDef ?? constructIdStateDef);
            setCustomerNameList(objCustomerName);
          }
        }
      });
    });

    // 工事IDの初期値が指定されている場合は工程情報を取得する
    if (constructIdStateDef) {
      setConstructListVal({
        value: constructIdStateDef,
        label: constructNameStateDef
      });
      renderCurrentConstructView();
    }
  }, [selectTab]);

  return {
    constructList,
    constructId,
    renderCurrentConstructView,
    isOpenAdd,
    setIsOpenAdd,
    customerName,
    isOpenAddOfGantt,
    setIsOpenAddOfGantt,
    handleOnClickAddOfGantt,
    isDownloading,
    isLoadingGantt,
    setIsLoadingGantt
  };
};
